







































































































































































import { ciBan } from '~/icons/source/solid/ban'
import { format } from '~/utils/date'
import {
  CLASSIFIED_VIEW_NS,
  ClassifiedViewState
} from '~/store/modules/shared/classifieds/view/state'
import ClassifiedBlacklistService from '~/services/blacklist/classified/ClassifiedBlacklistService'
import CListGroupItem from '~/components/shared/configurable/list-group/CListGroupItem.vue'
import CTextArea from '~/components/shared/configurable/form/input/CTextArea.vue'
import {
  computed,
  defineComponent,
  onMounted,
  ref
} from '~/utils/nuxt3-migration'
import { BlacklistInfo, Reason } from '~/models/blacklist/types'
import { useNamespacedStore } from '~/compositions/store'
import { useDep } from '~/compositions/dependency-container'
import { CategoryId } from '~/models/category/types'
import { useSnackbar } from '~/compositions/snackbar'
import CCategoryTreeSelect from '~/components/shared/configurable/category/tree-select/CCategoryTreeSelect.vue'
import { useRequestError } from '~/compositions/request-error'

const defaultBlacklistReasonId = 19

export default defineComponent({
  components: {
    CTextArea,
    CListGroupItem,
    CCategoryTreeSelect
  },
  setup() {
    const snackbar = useSnackbar()
    const { state: classifiedViewState } = useNamespacedStore<
      ClassifiedViewState
    >(CLASSIFIED_VIEW_NS)

    const isLoading = ref(false)
    const blacklistInfo = ref<BlacklistInfo | null>()
    const reasonId = ref(defaultBlacklistReasonId)
    const notes = ref('')
    const modalVisible = ref(false)
    const selectedCategory = ref()
    const blacklistCategories = ref([CategoryId.CARS])
    const childrenBlacklistCategories = ref([CategoryId.CARS])
    const parentsLimit = [
      CategoryId.PLOT,
      CategoryId.JOBS,
      CategoryId.RENTALS,
      CategoryId.JOBS_WANTED,
      CategoryId.JOBS_OFFERED,
      CategoryId.XYMA,
      CategoryId.PARTS
    ]
    const rootCategoryIds = [
      CategoryId.VEHICLES,
      CategoryId.PLOT,
      CategoryId.JOBS,
      CategoryId.XYMA,
      CategoryId.PARTS,
      CategoryId.RENTALS
    ]

    onMounted(() => {
      fetchBlacklistInfo()
    })

    const classifiedBlacklistService = useDep(ClassifiedBlacklistService)
    const handleRequestError = useRequestError()

    const classifiedId = computed(() => classifiedViewState.classified?.id)

    const groupListVariant = computed(() => {
      return isBlacklisted.value && !isCurrentCategoryInBlacklist.value
        ? 'warning'
        : undefined
    })

    const isBlacklisted = computed(() => blacklistInfo.value?.blacklist)

    const blacklistCreated = computed(
      () => blacklistInfo.value && format(blacklistInfo.value.blacklist.created)
    )

    const tooltipData = computed(() => {
      if (isBlacklisted.value && !isCurrentCategoryInBlacklist.value) {
        return {
          content: blacklistInfo.value?.blacklistCategoriesNames?.join(
            '<br/> '
          ),
          placement: 'bottom',
          html: true
        }
      }
      return undefined
    })

    const supportCategory = computed(
      () =>
        blacklistInfo.value?.blacklistReasons?.find(
          (r: Reason) => r.id === reasonId.value
        )?.supportCategory
    )

    const isCurrentCategoryInBlacklist = computed(() => {
      if (supportCategory.value && blacklistCategories.value.length) {
        return classifiedViewState.classified?.category_ids.some(id =>
          blacklistCategories.value.includes(id)
        )
      }
      return true
    })

    async function fetchBlacklistInfo() {
      isLoading.value = true

      try {
        blacklistInfo.value = await classifiedBlacklistService.getClassifiedBlacklistInfo(
          classifiedId.value!
        )
        if (blacklistInfo.value.blacklist) {
          reasonId.value = blacklistInfo.value.blacklist.reason.id as number
          blacklistCategories.value = blacklistInfo.value.blacklistCategories
        }
      } catch (e) {
        handleRequestError(e)
      } finally {
        isLoading.value = false
      }
    }

    function handleBlacklistButtonClick() {
      modalVisible.value = true
    }

    async function addToBlacklist(breakGroup: boolean) {
      isLoading.value = true

      try {
        const data = await classifiedBlacklistService.addClassifiedToBlacklist({
          classifiedId: classifiedId.value!,
          reasonId: reasonId.value,
          notes: notes.value,
          blacklistCategories: childrenBlacklistCategories.value,
          breakGroup
        })
        await fetchBlacklistInfo()

        snackbar.success(data.message)
      } catch (e) {
        handleRequestError(e)
      } finally {
        isLoading.value = false
      }
    }

    async function updateBlacklist() {
      isLoading.value = true

      try {
        await classifiedBlacklistService.updateBlacklist({
          classifiedId: classifiedId.value!,
          reasonId: reasonId.value,
          notes: notes.value,
          blacklistCategories: childrenBlacklistCategories.value,
          groupId: blacklistInfo.value?.blacklist?.groupId as number
        })
        await fetchBlacklistInfo()

        snackbar.saved()
      } catch (e) {
        handleRequestError(e)
      } finally {
        isLoading.value = false
      }
    }

    async function removeFromBlacklist() {
      isLoading.value = true

      try {
        const data = await classifiedBlacklistService.removeClassifiedFromBlacklist(
          classifiedId.value!
        )

        blacklistCategories.value = []
        childrenBlacklistCategories.value = []

        await fetchBlacklistInfo()

        snackbar.success(data.message)
      } catch (e) {
        handleRequestError(e)
      } finally {
        isLoading.value = false
      }
    }

    async function breakBlacklistGroups() {
      try {
        const data = await classifiedBlacklistService.breakBlacklist(
          classifiedId.value!
        )
        await fetchBlacklistInfo()

        snackbar.success(data.message)
      } catch (e) {
        handleRequestError(e)
      }
    }

    return {
      modalVisible,
      blacklistInfo,
      isBlacklisted,
      blacklistCreated,
      isLoading,
      notes,
      reasonId,
      rootCategoryIds,
      handleBlacklistButtonClick,
      addToBlacklist,
      removeFromBlacklist,
      breakBlacklistGroups,
      ciBan,
      supportCategory,
      selectedCategory,
      blacklistCategories,
      updateBlacklist,
      childrenBlacklistCategories,
      parentsLimit,
      isCurrentCategoryInBlacklist,
      groupListVariant,
      tooltipData
    }
  }
})
